/**
 * EmptyState Component
 *
 * Korat Health Style: Friendly empty state for when data is not available
 * Replaces sad "0 คน" with icon + helpful message
 */

import { Inbox, LucideIcon } from 'lucide-react'

export interface EmptyStateProps {
  /** Icon to display (default: Inbox) */
  icon?: LucideIcon
  /** Message to show (default: "ยังไม่มีข้อมูลในระบบ") */
  message?: string
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Custom className */
  className?: string
}

export function EmptyState({
  icon: Icon = Inbox,
  message = 'ยังไม่มีข้อมูลในระบบ',
  size = 'md',
  className = '',
}: EmptyStateProps) {
  const sizeClasses = {
    sm: {
      icon: 'w-6 h-6',
      text: 'text-xs',
      gap: 'gap-2',
      padding: 'py-3',
    },
    md: {
      icon: 'w-8 h-8',
      text: 'text-sm',
      gap: 'gap-2',
      padding: 'py-4',
    },
    lg: {
      icon: 'w-12 h-12',
      text: 'text-base',
      gap: 'gap-3',
      padding: 'py-6',
    },
  }

  const sizes = sizeClasses[size]

  return (
    <div
      className={`
        flex flex-col items-center ${sizes.gap} ${sizes.padding}
        ${className}
      `}
      role="status"
      aria-live="polite"
    >
      <Icon
        className={`${sizes.icon} text-slate-300`}
        aria-hidden="true"
      />
      <p className={`${sizes.text} text-slate-500 text-center leading-relaxed`}>
        {message}
      </p>
    </div>
  )
}
